import React, { useState, useEffect, FormEvent } from 'react';
import { 
  Sprout, 
  Droplets, 
  Armchair, 
  Flame, 
  Home as HomeIcon, 
  TreePine, 
  Utensils, 
  Calendar, 
  Lock, 
  Eye, 
  ArrowRight, 
  Phone, 
  MapPin, 
  ShieldCheck, 
  X,
  Sparkles,
  ArrowUpRight,
  Instagram,
  Clock,
  TrendingUp,
  Camera,
  Heart,
  BookOpen,
  LayoutGrid,
  Play
} from 'lucide-react';

import { Memory, FarmEvent, FarmFeature, HomeSettings, Feedback, getImageStyleFromUrl, cleanImageUrl } from './types';

import Header from './components/Header';
import Footer from './components/Footer';
import Hero from './components/Hero';
import EventDetailModal from './components/EventDetailModal';
import FeatureDetailModal from './components/FeatureDetailModal';
import AdminPanel from './components/AdminPanel';
import MemoryCard from './components/MemoryCard';
import MemoryDetailModal from './components/MemoryDetailModal';

export default function App() {
  // Page routing state
  const [currentPage, setCurrentPage] = useState<string>('home');

  // Media, Events, and Features dynamic states
  const [memories, setMemories] = useState<Memory[]>([]);
  const [events, setEvents] = useState<FarmEvent[]>([]);
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);

  // Home page hero settings
  const [homeSettings, setHomeSettings] = useState<HomeSettings>({
    hero_image_url: '',
    hero_title: '',
    hero_subtitle: '',
    hero_button_text: ''
  });
  const [features, setFeatures] = useState<FarmFeature[]>([]);
  const [selectedFeature, setSelectedFeature] = useState<FarmFeature | null>(null);

  // Authentication State
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [adminPasswordInput, setAdminPasswordInput] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');

  // UI state overlays
  const [selectedEvent, setSelectedEvent] = useState<FarmEvent | null>(null);
  const [selectedMemory, setSelectedMemory] = useState<Memory | null>(null);

  // Events page view mode toggle
  const [eventsViewMode, setEventsViewMode] = useState<'cards' | 'timeline'>('cards');

  // Memories page view mode toggle
  const [memoriesViewMode, setMemoriesViewMode] = useState<'cards' | 'timeline'>('cards');

  // Footer Modal State
  const [footerModalType, setFooterModalType] = useState<'contact' | 'directions' | 'privacy' | null>(null);

  // Guest Feedback Form State
  const [feedbackUsername, setFeedbackUsername] = useState<string>('');
  const [feedbackContent, setFeedbackContent] = useState<string>('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState<boolean>(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState<boolean>(false);
  const [feedbackError, setFeedbackError] = useState<string>('');

  // 1. Initialize data from MySQL
  const loadAllDataFromMySQL = async () => {
    try {
      const [memoriesRes, eventsRes, featuresRes, homeSettingsRes, feedbacksRes] = await Promise.all([
        fetch('/api/memories').then((r) => r.json()),
        fetch('/api/events').then((r) => r.json()),
        fetch('/api/features').then((r) => r.json()),
        fetch('/api/home-settings').then((r) => r.json()),
        fetch('/api/feedbacks').then((r) => r.json())
      ]);
      
      if (Array.isArray(memoriesRes)) {
        const sorted = [...memoriesRes].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        setMemories(sorted);
      }
      if (Array.isArray(eventsRes)) {
        setEvents(eventsRes);
      }
      if (Array.isArray(featuresRes)) {
        setFeatures(featuresRes);
      }
      if (Array.isArray(feedbacksRes)) {
        setFeedbacks(feedbacksRes);
      }
      if (homeSettingsRes && !homeSettingsRes.error) {
        setHomeSettings(homeSettingsRes);
      }
    } catch (err) {
      console.error('❌ Could not fetch from MySQL DB.', err);
      setMemories([]);
      setEvents([]);
      setFeatures([]);
      setFeedbacks([]);
    }
  };

  useEffect(() => {
    loadAllDataFromMySQL();

    // Auth status check
    const authStatus = sessionStorage.getItem('mithran_admin_auth');
    if (authStatus === 'true') {
      setIsAdmin(true);
    }
  }, []);

  const handleFeedbackSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!feedbackUsername.trim() || !feedbackContent.trim()) return;

    setIsSubmittingFeedback(true);
    setFeedbackError('');

    try {
      const response = await fetch('/api/feedbacks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: feedbackUsername.trim(),
          feedback: feedbackContent.trim()
        })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setFeedbackSuccess(true);
        setFeedbackUsername('');
        setFeedbackContent('');
        await loadAllDataFromMySQL();
      } else {
        setFeedbackError(data.error || 'Failed to submit feedback.');
      }
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setFeedbackError('Network error. Please try again.');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // 1b. Google Analytics Tracking Hooks
  // Track page views when simulated tab/page route changes
  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'page_view', {
        page_title: currentPage.charAt(0).toUpperCase() + currentPage.slice(1) + ' | Mithran Farm House',
        page_location: window.location.origin + '/' + currentPage,
        page_path: '/' + currentPage
      });
    }
  }, [currentPage]);

  // Scroll to top of window on page changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  // Track event details modal interactions
  useEffect(() => {
    if (selectedEvent && typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'select_content', {
        content_type: 'Event Detail',
        item_id: selectedEvent.id,
        item_name: selectedEvent.title
      });
    }
  }, [selectedEvent]);

  // Track footer info modal views (Contact, Directions, Privacy)
  useEffect(() => {
    if (footerModalType && typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'view_footer_modal', {
        modal_type: footerModalType
      });
    }
  }, [footerModalType]);

  // -------------------------------------------------------------
  // MUTATION SYNCERS (Saves to MySQL dynamically with local cache)
  // -------------------------------------------------------------
  const handleUpdateMemories = async (newMemories: Memory[]) => {
    // 1. Update React state immediately for snappy responsive UI
    const prevMemories = [...memories];
    const sorted = [...newMemories].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    setMemories(sorted);

    // 2. Identify and execute delta-updates asynchronously to MySQL
    try {
      if (newMemories.length < prevMemories.length) {
        // Deletion
        const deleted = prevMemories.find((p) => !newMemories.some((n) => n.id === p.id));
        if (deleted) {
          await fetch(`/api/memories/${deleted.id}`, { method: 'DELETE' });
        }
      } else if (newMemories.length > prevMemories.length) {
        // Addition
        const added = newMemories.find((n) => !prevMemories.some((p) => p.id === n.id));
        if (added) {
          await fetch('/api/memories', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(added)
          });
        }
      } else {
        // Update / Modification
        const edited = newMemories.find((n) => {
          const old = prevMemories.find((p) => p.id === n.id);
          return old && JSON.stringify(old) !== JSON.stringify(n);
        });
        if (edited) {
          await fetch('/api/memories', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(edited)
          });
        }
      }
    } catch (e) {
      console.error('MySQL Memories Sync failed:', e);
    }
  };

  const handleUpdateEvents = async (newEvents: FarmEvent[]) => {
    const prevEvents = [...events];
    const sorted = [...newEvents].sort((a, b) => b.dateRaw.localeCompare(a.dateRaw));
    
    setEvents(sorted);

    try {
      if (newEvents.length < prevEvents.length) {
        // Deletion
        const deleted = prevEvents.find((p) => !newEvents.some((n) => n.id === p.id));
        if (deleted) {
          await fetch(`/api/events/${deleted.id}`, { method: 'DELETE' });
        }
      } else if (newEvents.length > prevEvents.length) {
        // Addition
        const added = newEvents.find((n) => !prevEvents.some((p) => p.id === n.id));
        if (added) {
          await fetch('/api/events', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(added)
          });
        }
      } else {
        // Update / Modification
        const edited = newEvents.find((n) => {
          const old = prevEvents.find((p) => p.id === n.id);
          return old && JSON.stringify(old) !== JSON.stringify(n);
        });
        if (edited) {
          await fetch('/api/events', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(edited)
          });
        }
      }
    } catch (e) {
      console.error('MySQL Events Sync failed:', e);
    }
  };

  const handleUpdateFeatures = async (newFeatures: FarmFeature[]) => {
    const prevFeatures = [...features];
    setFeatures(newFeatures);

    try {
      if (newFeatures.length < prevFeatures.length) {
        // Deletion
        const deleted = prevFeatures.find((p) => !newFeatures.some((n) => n.id === p.id));
        if (deleted) {
          await fetch(`/api/features/${deleted.id}`, { method: 'DELETE' });
        }
      } else if (newFeatures.length > prevFeatures.length) {
        // Addition
        const added = newFeatures.find((n) => !prevFeatures.some((p) => p.id === n.id));
        if (added) {
          await fetch('/api/features', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(added)
          });
        }
      } else {
        // Update / Modification
        const edited = newFeatures.find((n) => {
          const old = prevFeatures.find((p) => p.id === n.id);
          return old && JSON.stringify(old) !== JSON.stringify(n);
        });
        if (edited) {
          await fetch('/api/features', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(edited)
          });
        }
      }
    } catch (e) {
      console.error('MySQL Features Sync failed:', e);
    }
  };

  const handleResetData = async () => {
    try {
      await fetch('/api/reset', { method: 'POST' });
      // Reload fresh data from MySQL after reset
      const [memoriesRes, eventsRes, featuresRes] = await Promise.all([
        fetch('/api/memories').then((r) => r.json()),
        fetch('/api/events').then((r) => r.json()),
        fetch('/api/features').then((r) => r.json())
      ]);
      if (Array.isArray(memoriesRes)) {
        const sorted = [...memoriesRes].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        setMemories(sorted);
      }
      if (Array.isArray(eventsRes)) setEvents(eventsRes);
      if (Array.isArray(featuresRes)) setFeatures(featuresRes);
    } catch (e) {
      console.error('Failed to trigger database reset on server:', e);
    }
  };

  const handleUpdateHomeSettings = async (newSettings: HomeSettings) => {
    setHomeSettings(newSettings);
    try {
      await fetch('/api/home-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSettings)
      });
    } catch (e) {
      console.error('Failed to save home settings:', e);
    }
  };

  // -------------------------------------------------------------
  // AUTHENTICATION LOGIC
  // -------------------------------------------------------------
  const handleAdminLoginSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/admin/verify-passcode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode: adminPasswordInput.trim() })
      });
      const data = await response.json();
      if (data.success) {
        setIsAdmin(true);
        sessionStorage.setItem('mithran_admin_auth', 'true');
        setAdminPasswordInput('');
        setPasswordError('');
      } else {
        setPasswordError('Invalid secure passcode. Please double check with family.');
      }
    } catch (err) {
      console.error('Error verifying passcode:', err);
      setPasswordError('Network error while verifying passcode.');
    }
  };

  const handleLogout = () => {
    setIsAdmin(false);
    sessionStorage.removeItem('mithran_admin_auth');
    setCurrentPage('home');
  };

  // -------------------------------------------------------------
  // EVENTS LOGIC GROUPING
  // -------------------------------------------------------------
  const upcomingEvents = events.filter(e => e.category === 'upcoming');
  const pastEvents = events.filter(e => e.category === 'past');
  // Dynamic images for home page Bento grid categories
  const getDynamicHomeImages = () => {
    // 1. Memories image (first image memory)
    const imgMemory = memories.find(m => m.mediaType === 'image' && m.mediaUrl);
    let memoriesImg = "https://images.unsplash.com/photo-1515003197210-e0cd71810b5f?auto=format&fit=crop&q=80&w=1200";
    if (imgMemory) {
      try {
        const url = imgMemory.mediaUrl;
        if (url.trim().startsWith('[')) {
          const parsed = JSON.parse(url);
          if (Array.isArray(parsed) && parsed[0]) {
            memoriesImg = parsed[0];
          }
        } else {
          memoriesImg = url;
        }
      } catch (e) {
        memoriesImg = imgMemory.mediaUrl;
      }
    }

    // 2. Events image (first event thumbnail)
    const eventImg = events.find(e => e.thumbnailUrl)?.thumbnailUrl || "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&q=80&w=600";

    // 3. Features image (first feature image)
    const featureImg = features.find(f => f.imageUrl)?.imageUrl || "https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?auto=format&fit=crop&q=80&w=600";

    return { memoriesImg, eventImg, featureImg };
  };

  const { memoriesImg, eventImg, featureImg } = getDynamicHomeImages();

  const handleNavigate = (page: string) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div id="root-container" className="min-h-screen flex flex-col font-sans antialiased bg-[#FDFCF9] text-[#1A241F]">
      
      {/* Navigation Header */}
      <Header
        currentPage={currentPage}
        onNavigate={handleNavigate}
        isAdmin={isAdmin}
        onLogout={handleLogout}
      />

      <main className="flex-grow">
        
        {/* ========================================================= */}
        {/* 1. HOME VIEW */}
        {/* ========================================================= */}
        {currentPage === 'home' && (
          <div className="animate-fadeIn">
            {/* Immersive Sanctuary Hero */}
            <Hero
              onExplore={() => {
                const gallerySection = document.getElementById('pages-of-story');
                if (gallerySection) gallerySection.scrollIntoView({ behavior: 'smooth' });
              }}
              backgroundImage={homeSettings.hero_image_url || undefined}
              title={homeSettings.hero_title || undefined}
              subtitle={homeSettings.hero_subtitle || undefined}
              buttonText={homeSettings.hero_button_text || undefined}
            />

            {/* ---- Sanctuary Almanac (Heritage in Numbers) ---- */}
            <section id="archive-stats" className="w-full bg-[#FAF7F0] border-b border-[#E6E1D4] py-16 md:py-20">
              <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="text-center mb-12">
                  <span className="estate-chip mb-3">
                    <Sprout size={12} className="text-emerald-700" />
                    Sanctuary Almanac
                  </span>
                  <h2 className="font-serif text-3xl sm:text-4xl text-[#1A241F] font-semibold tracking-tight">
                    Our Living Story in Harmony with Nature
                  </h2>
                  <p className="text-sm text-[#65756C] mt-2 max-w-md mx-auto">
                    A private sanctuary where milestones are preserved, harvests cherished, and stories passed down.
                  </p>
                </div>

                {/* 4 Warm Organic Estate Metric Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                  {[
                    {
                      icon: <Heart size={20} className="text-emerald-700" />,
                      label: 'Cherished Stories',
                      caption: 'Reflections & moments',
                      value: memories.length,
                    },
                    {
                      icon: <Calendar size={20} className="text-amber-700" />,
                      label: 'Gatherings & Seasons',
                      caption: 'Family celebrations',
                      value: events.length,
                    },
                    {
                      icon: <TreePine size={20} className="text-emerald-800" />,
                      label: 'Years of Lore',
                      caption: 'Rooted across seasons',
                      value: (() => {
                        const allDates = [...memories.map(m => m.date), ...events.map(e => e.dateRaw)]
                          .filter(Boolean).map(d => new Date(d).getFullYear()).filter(y => !isNaN(y));
                        if (allDates.length === 0) return 0;
                        return new Date().getFullYear() - Math.min(...allDates) + 1;
                      })(),
                    },
                    {
                      icon: <Camera size={20} className="text-amber-600" />,
                      label: 'Captured Moments',
                      caption: 'Photographs & reels',
                      value: (() => {
                        const p = memories.filter(m => m.mediaType === 'image').length;
                        const eg = events.reduce((a, e) => a + (e.galleryUrls?.length || 0) + (e.thumbnailUrl ? 1 : 0), 0);
                        return p + eg;
                      })(),
                    },
                  ].map((stat, idx) => (
                    <div
                      key={stat.label}
                      className="card-estate p-6 sm:p-8 flex flex-col justify-between bg-white border border-[#E6E1D4] hover:border-emerald-700/30 transition-all duration-300"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-10 h-10 rounded-xl bg-[#F0FDF4] border border-[#DCFCE7] flex items-center justify-center">
                          {stat.icon}
                        </div>
                        <span className="font-sans text-[10px] font-semibold text-[#8E9E95] uppercase tracking-wider">
                          0{idx + 1}
                        </span>
                      </div>
                      <div>
                        <span className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A241F] tracking-tight block">
                          {stat.value}
                        </span>
                        <span className="font-sans text-xs sm:text-sm font-semibold text-[#2C3E35] block mt-1">
                          {stat.label}
                        </span>
                        <span className="font-sans text-[11px] text-[#65756C] block mt-0.5">
                          {stat.caption}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Chapters of the Estate / Stories Grid */}
            <section id="pages-of-story" className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
              <div className="mb-14">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                  <div>
                    <span className="estate-chip mb-3">
                      <Sparkles size={11} className="text-emerald-700" />
                      Chapters of the Estate
                    </span>
                    <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-[#1A241F] tracking-tight">
                      Explore Our Sanctuary
                    </h2>
                  </div>
                  <p className="font-sans text-sm sm:text-base text-[#65756C] max-w-md">
                    Glimpses into the life, quiet traditions, and timeless warmth shared within these acres.
                  </p>
                </div>
              </div>

              {/* Media card grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                {/* 1. Wide memories card — md:col-span-2 */}
                <div
                  onClick={() => setCurrentPage('memories')}
                  className="card-media md:col-span-2 cursor-pointer group h-[380px] shadow-sm hover:shadow-xl transition-all duration-300"
                >
                  <img
                    src={memoriesImg}
                    alt="Memories Archive"
                    referrerPolicy="no-referrer"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/90 via-emerald-950/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-10 text-white">
                    <span className="estate-chip-amber mb-3 shadow-sm">
                      Living Archive
                    </span>
                    <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-2 leading-tight">
                      Memories &amp; Stories Feed
                    </h3>
                    <p className="font-sans text-stone-200 text-sm max-w-lg mb-4 opacity-90 leading-relaxed">
                      A visual chronicle of quiet mornings, raised organic vegetable beds, and seasonal harvest moments.
                    </p>
                    <span className="inline-flex items-center gap-2 text-amber-300 text-sm font-semibold group-hover:translate-x-1 transition-transform">
                      Browse Full Memory Archive <ArrowRight size={14} />
                    </span>
                  </div>
                </div>

                {/* 2. Events card */}
                <div
                  onClick={() => setCurrentPage('events')}
                  className="card-media cursor-pointer group h-[380px] shadow-sm hover:shadow-xl transition-all duration-300"
                >
                  <img
                    src={eventImg}
                    alt="Gatherings & Events"
                    referrerPolicy="no-referrer"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/90 via-emerald-950/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <span className="estate-chip-amber mb-3 shadow-sm">
                      Gatherings
                    </span>
                    <h3 className="font-serif text-2xl font-semibold text-white mb-2 leading-tight">
                      Events &amp; Traditions
                    </h3>
                    <p className="font-sans text-stone-200 text-xs sm:text-sm mb-4 opacity-90 leading-relaxed">
                      Harvest suppers, summer solstice bonfires, and morning perimeter walks.
                    </p>
                    <span className="inline-flex items-center gap-2 text-amber-300 text-sm font-semibold group-hover:translate-x-1 transition-transform">
                      View Calendar <ArrowRight size={14} />
                    </span>
                  </div>
                </div>

                {/* 3. Features card */}
                <div
                  onClick={() => setCurrentPage('features')}
                  className="card-media cursor-pointer group h-[360px] shadow-sm hover:shadow-xl transition-all duration-300"
                >
                  <img
                    src={featureImg}
                    alt="Estate Grounds"
                    referrerPolicy="no-referrer"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/90 via-emerald-950/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <span className="estate-chip-amber mb-3 shadow-sm">
                      The Grounds
                    </span>
                    <h3 className="font-serif text-2xl font-semibold text-white mb-2 leading-tight">
                      Estate &amp; Grounds
                    </h3>
                    <p className="font-sans text-stone-200 text-xs sm:text-sm mb-4 opacity-90 leading-relaxed">
                      Explore the antique stone well, veranda armchairs, and evening fire pit.
                    </p>
                    <span className="inline-flex items-center gap-2 text-amber-300 text-sm font-semibold group-hover:translate-x-1 transition-transform">
                      Explore Details <ArrowRight size={14} />
                    </span>
                  </div>
                </div>

                {/* 4. Luxury Heritage Lore Quote Band */}
                <div className="md:col-span-2 rounded-3xl p-8 sm:p-12 bg-gradient-to-br from-[#14532D] via-[#0F3820] to-[#0A2616] text-white flex flex-col justify-between relative overflow-hidden shadow-lg">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-600/10 rounded-full blur-3xl pointer-events-none" />
                  <div className="text-amber-400/20 font-serif text-7xl select-none leading-none -mb-4">
                    “
                  </div>
                  <div className="relative z-10">
                    <p className="font-serif text-xl sm:text-2xl lg:text-3xl font-normal leading-relaxed text-emerald-50 mb-6 italic">
                      A house is made of brick and stone; a sanctuary is built of love, shared laughter, and quiet memories.
                    </p>
                    <div className="border-t border-emerald-700/40 pt-4 flex items-center justify-between text-xs text-emerald-200/70">
                      <span className="font-semibold uppercase tracking-widest text-amber-300">
                        Family Lore
                      </span>
                      <span>Mithran Estate · Sirudavoor</span>
                    </div>
                  </div>
                </div>

                {/* 5. Instagram Chronicles Band */}
                <a
                  href="https://www.instagram.com/mithran_farm_house/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="md:col-span-3 rounded-3xl p-6 sm:p-8 bg-[#FAF7F0] border border-[#E6E1D4] hover:border-emerald-600/40 transition-all duration-300 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 group shadow-xs hover:shadow-md"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 via-rose-500 to-purple-600 flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform">
                      <Instagram size={28} />
                    </div>
                    <div>
                      <span className="estate-chip text-[10px] mb-1">
                        Social Chronicles
                      </span>
                      <h3 className="font-serif text-xl font-semibold text-[#1A241F] group-hover:text-emerald-800 transition-colors">
                        @mithran_farm_house
                      </h3>
                      <p className="text-xs text-[#65756C] mt-0.5">
                        Follow our daily farm updates, seasonal harvests, and morning mist over the fields.
                      </p>
                    </div>
                  </div>

                  <div className="btn-estate-primary text-xs py-2.5 px-5 gap-2 self-start sm:self-center">
                    <span>Follow Our Journey</span>
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </a>

              </div>
            </section>

            {/* Recent Moments Spotlight Preview */}
            {memories.length > 0 && (
              <section className="bg-[#FAF7F0] border-t border-[#E6E1D4] py-20">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
                    <div>
                      <span className="estate-chip mb-2">
                        <Camera size={12} className="text-emerald-700" />
                        Freshly Archived
                      </span>
                      <h2 className="font-serif text-3xl font-semibold text-[#1A241F]">
                        Recent Moments from the Sanctuary
                      </h2>
                    </div>
                    <button
                      onClick={() => setCurrentPage('memories')}
                      className="btn-estate-secondary text-xs self-start"
                    >
                      View All Memories ({memories.length})
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {memories.slice(0, 3).map((m) => (
                      <MemoryCard
                        key={m.id}
                        memory={m}
                        onSelect={(item) => setSelectedMemory(item)}
                      />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Guest Book Highlights */}
            {feedbacks.length > 0 && (
              <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
                <div className="text-center mb-12">
                  <span className="estate-chip mb-2">
                    <Heart size={12} className="text-rose-600" />
                    Reflections &amp; Warm Words
                  </span>
                  <h2 className="font-serif text-3xl font-semibold text-[#1A241F]">
                    From the Guest Book
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                  {feedbacks.slice(0, 2).map((f) => (
                    <div
                      key={f.id}
                      className="card-estate p-6 bg-white border border-[#E6E1D4] flex flex-col justify-between"
                    >
                      <p className="font-serif italic text-stone-700 text-base leading-relaxed mb-6">
                        "{f.feedback}"
                      </p>
                      <div className="flex items-center justify-between border-t border-[#E6E1D4]/60 pt-4 text-xs text-[#65756C]">
                        <span className="font-semibold text-[#14532D]">
                          — {f.username}
                        </span>
                        <span>
                          {new Date(f.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-center mt-10">
                  <button
                    onClick={() => setCurrentPage('feedback')}
                    className="btn-estate-primary text-xs py-2.5 px-6"
                  >
                    Leave a Note in the Guest Book
                  </button>
                </div>
              </section>
            )}

          </div>
        )}

        {/* ========================================================= */}
        {/* 5. GUEST BOOK (FEEDBACK) VIEW */}
        {/* ========================================================= */}
        {currentPage === 'feedback' && (
          <div className="animate-fadeIn">
            {/* Page Header */}
            <div className="w-full py-16 sm:py-20 bg-[#FAF7F0] border-b border-[#E6E1D4]">
              <div className="max-w-7xl mx-auto px-6 md:px-12">
                <span className="estate-chip mb-3">
                  <Heart size={12} className="text-rose-600" />
                  Sanctuary Guest Book
                </span>
                <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-[#1A241F] tracking-tight mb-3">
                  Warm Words &amp; Shared Reflections
                </h1>
                <p className="font-sans text-sm sm:text-base text-[#65756C] max-w-xl">
                  Quiet memories, family stories, and heartfelt notes from guests and relatives who have stayed beneath the shade of Mithran Farm House.
                </p>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
                
                {/* Left Column: Guest Book Form */}
                <div className="lg:col-span-1 space-y-6">
                  <div className="card-estate p-6 sm:p-8 bg-white border border-[#E6E1D4] shadow-sm">
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-800 flex items-center justify-center">
                        <BookOpen size={16} />
                      </div>
                      <h3 className="font-serif text-xl font-semibold text-[#1A241F]">
                        Sign the Guest Book
                      </h3>
                    </div>
                    <p className="text-xs text-[#65756C] leading-relaxed mb-5">
                      Have you spent peaceful mornings or harvest suppers at Mithran? We warmly invite you to leave a memory or wish.
                    </p>

                    <form onSubmit={handleFeedbackSubmit} className="space-y-4">
                      {feedbackSuccess ? (
                        <div className="text-center py-6 space-y-3 animate-fadeIn bg-emerald-50/70 border border-emerald-200/60 rounded-2xl p-4">
                          <div className="w-12 h-12 bg-emerald-100 text-emerald-800 rounded-full flex items-center justify-center mx-auto">
                            <ShieldCheck size={24} />
                          </div>
                          <h4 className="font-serif text-lg font-semibold text-[#14532D]">Note Submitted with Warmth</h4>
                          <p className="text-xs text-[#37463E] leading-relaxed">
                            Thank you! Your note has been received and will be published once approved by the family administrator.
                          </p>
                          <button
                            type="button"
                            onClick={() => setFeedbackSuccess(false)}
                            className="text-xs font-semibold text-[#14532D] hover:underline cursor-pointer"
                          >
                            Submit another reflection
                          </button>
                        </div>
                      ) : (
                        <>
                          <div>
                            <label htmlFor="feedback-username" className="block text-xs font-semibold uppercase tracking-wider text-[#65756C] mb-1.5">
                              Your Name
                            </label>
                            <input
                              type="text"
                              id="feedback-username"
                              required
                              value={feedbackUsername}
                              onChange={(e) => setFeedbackUsername(e.target.value)}
                              placeholder="e.g. Ramesh, Priya, Aunt Lakshmi"
                              className="w-full bg-[#FAF9F6] border border-[#E6E1D4] focus:border-[#14532D] focus:ring-1 focus:ring-[#14532D]/30 rounded-xl px-4 py-2.5 text-sm text-[#1A241F] focus:outline-none transition-all"
                            />
                          </div>

                          <div>
                            <label htmlFor="feedback-content" className="block text-xs font-semibold uppercase tracking-wider text-[#65756C] mb-1.5">
                              Your Reflection / Note
                            </label>
                            <textarea
                              id="feedback-content"
                              required
                              rows={4}
                              value={feedbackContent}
                              onChange={(e) => setFeedbackContent(e.target.value)}
                              placeholder="Share your stay experience, favorite corner, or warm thoughts..."
                              className="w-full bg-[#FAF9F6] border border-[#E6E1D4] focus:border-[#14532D] focus:ring-1 focus:ring-[#14532D]/30 rounded-xl px-4 py-2.5 text-sm text-[#1A241F] focus:outline-none transition-all resize-none"
                            />
                          </div>

                          {feedbackError && (
                            <p className="text-xs text-red-600 font-semibold">{feedbackError}</p>
                          )}

                          <button
                            type="submit"
                            disabled={isSubmittingFeedback}
                            className="btn-estate-primary w-full justify-center text-xs py-3"
                            style={{ opacity: isSubmittingFeedback ? 0.6 : 1 }}
                          >
                            {isSubmittingFeedback ? 'Preserving Note...' : 'Add to Sanctuary Ledger'}
                          </button>
                        </>
                      )}
                    </form>
                  </div>
                </div>

                {/* Right Column: Feedbacks List */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="flex items-center justify-between border-b border-[#E6E1D4] pb-4">
                    <h3 className="font-serif text-2xl font-semibold text-[#1A241F]">
                      Notes from Loved Ones &amp; Guests
                    </h3>
                    <span className="estate-chip text-[10px]">
                      {feedbacks.length} Published Notes
                    </span>
                  </div>

                  {feedbacks.length === 0 ? (
                    <div className="text-center py-20 bg-white border border-[#E6E1D4] rounded-3xl p-8">
                      <p className="font-serif italic text-base text-[#65756C]">
                        No guest notes published yet. Be the first to leave a warm reflection!
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {feedbacks.map((f) => (
                        <div
                          key={f.id}
                          className="card-estate p-6 bg-white border border-[#E6E1D4] shadow-xs hover:shadow-md transition-all flex flex-col justify-between"
                        >
                          <div>
                            <div className="text-amber-500 font-serif text-3xl leading-none mb-2 select-none">
                              “
                            </div>
                            <p className="font-serif text-[15px] italic text-[#24332B] leading-relaxed mb-6">
                              "{f.feedback}"
                            </p>
                          </div>
                          <div className="flex justify-between items-center border-t border-[#FAF7F0] pt-4 text-xs text-[#65756C]">
                            <span className="font-semibold text-[#14532D]">
                              — {f.username}
                            </span>
                            <span className="text-[11px] text-[#8E9E95]">
                              {new Date(f.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* 2. GALLERY VIEW */}
        {/* ========================================================= */}
        {currentPage === 'memories' && (() => {
          // Merge database memories with static testimonial/quote cards
          type MergedItem =
            | { type: 'memory'; memory: Memory }
            | { type: 'quote'; quoteType: 'sharma' | 'marcus' | 'aditi' };

          const mergedItems: MergedItem[] = [];
          let dbIndex = 0;

          // Target specific positions to insert quote cards to mimic the masonry layout from the design mockup
          for (let i = 0; i < memories.length + 3; i++) {
            if (i === 1) {
              mergedItems.push({ type: 'quote', quoteType: 'sharma' });
            } else if (i === 3) {
              mergedItems.push({ type: 'quote', quoteType: 'aditi' });
            } else if (i === 5) {
              mergedItems.push({ type: 'quote', quoteType: 'marcus' });
            } else if (dbIndex < memories.length) {
              mergedItems.push({ type: 'memory', memory: memories[dbIndex] });
              dbIndex++;
            }
          }

          // Fallbacks to make sure all quotes are added if memories are few
          const addedQuotes = mergedItems.filter(item => item.type === 'quote').map(item => (item as any).quoteType);
          if (!addedQuotes.includes('sharma')) mergedItems.push({ type: 'quote', quoteType: 'sharma' });
          if (!addedQuotes.includes('aditi')) mergedItems.push({ type: 'quote', quoteType: 'aditi' });
          if (!addedQuotes.includes('marcus')) mergedItems.push({ type: 'quote', quoteType: 'marcus' });

          return (
            <div className="w-full min-h-screen animate-fadeIn bg-[#FDFCF9]">
              {/* Page Header */}
              <section className="w-full py-16 sm:py-20 bg-[#FAF7F0] border-b border-[#E6E1D4]">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div>
                      <span className="estate-chip mb-3">
                        <Camera size={12} className="text-emerald-700" />
                        Sanctuary Archive
                      </span>
                      <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-[#1A241F] tracking-tight mb-3">
                        Captured Moments &amp; Quiet Reflections
                      </h1>
                    </div>
                    <p className="font-sans text-sm sm:text-base text-[#65756C] max-w-md">
                      A living chronicle of shared laughter, sunlit morning dew, and the simple joys of life at Mithran Farm House.
                    </p>
                  </div>
                </div>
              </section>

              {/* View Mode & Filter Toggle */}
              <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10 pb-6 flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-[#65756C] uppercase tracking-wider">
                    Viewing:
                  </span>
                  <span className="estate-chip text-[11px]">
                    {memories.length} Memories
                  </span>
                </div>

                <div className="inline-flex items-center bg-[#FAF7F0] border border-[#E6E1D4] rounded-full p-1 gap-1">
                  <button
                    onClick={() => setMemoriesViewMode('cards')}
                    className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                      memoriesViewMode === 'cards'
                        ? 'bg-[#14532D] text-white shadow-sm'
                        : 'text-[#65756C] hover:text-[#1A241F]'
                    }`}
                  >
                    <LayoutGrid size={13} />
                    Curated Cards
                  </button>
                  <button
                    onClick={() => setMemoriesViewMode('timeline')}
                    className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                      memoriesViewMode === 'timeline'
                        ? 'bg-[#14532D] text-white shadow-sm'
                        : 'text-[#65756C] hover:text-[#1A241F]'
                    }`}
                  >
                    <Clock size={13} />
                    Chronological Ribbon
                  </button>
                </div>
              </div>

              {/* Memories List / Timeline Section */}
              <section className="max-w-7xl mx-auto px-4 md:px-8 pb-16">
                {memories.length === 0 ? (
                  <div className="text-center py-20 bg-white border border-[#E5E2D9] rounded-3xl max-w-md mx-auto shadow-sm">
                    <p className="handwritten text-lg text-[#6B705C] italic">
                      No memories have been posted yet. Check back soon!
                    </p>
                  </div>
                ) : (
                  <>
                    {memoriesViewMode === 'cards' && (
                      <div className="columns-1 md:columns-2 lg:columns-3 gap-8 [column-fill:_balance] w-full">
                        {mergedItems.map((item, index) => {
                          if (item.type === 'quote') {
                            if (item.quoteType === 'sharma') {
                              return (
                                <div
                                  key={`quote-sharma-${index}`}
                                  className="break-inside-avoid mb-8 p-8 bg-[#EBE9E4] border-l-4 border-[#2C241E] rounded-r-2xl flex flex-col justify-between shadow-sm min-h-[180px]"
                                >
                                  <p className="font-serif text-lg text-[#2D2D2A] leading-relaxed font-semibold">
                                    "Our kids forgot they had iPads. They spent the whole weekend chasing butterflies and learning about the cows."
                                  </p>
                                  <span className="text-xs font-mono font-bold text-[#6B705C] mt-6">
                                    — The Sharma Family
                                  </span>
                                </div>
                              );
                            } else if (item.quoteType === 'marcus') {
                              return (
                                <div
                                  key={`quote-marcus-${index}`}
                                  className="break-inside-avoid mb-8 p-8 bg-[#1E332A] text-white rounded-3xl border border-[#E5E2D9] flex flex-col justify-between shadow-sm min-h-[220px]"
                                >
                                  <div className="text-emerald-400 mb-6">
                                    <TreePine size={28} />
                                  </div>
                                  <p className="font-serif text-lg italic text-[#F9F7F2] leading-relaxed">
                                    "Simplicity is the ultimate luxury, Mithran reminded us of that."
                                  </p>
                                  <span className="text-xs font-mono font-bold text-[#E5E2D9] mt-6">
                                    — Marcus A., Solo Traveler
                                  </span>
                                </div>
                              );
                            } else {
                              // aditi & rohan
                              return (
                                <div
                                  key={`quote-aditi-${index}`}
                                  className="break-inside-avoid mb-8 p-8 bg-[#D5E0C5] text-[#2D2D2A] rounded-3xl border border-[#E5E2D9] flex flex-col justify-between shadow-sm min-h-[220px]"
                                >
                                  <div className="font-serif text-4xl text-[#6B705C] font-bold mb-4 opacity-70">
                                    99
                                  </div>
                                  <p className="font-serif text-lg text-[#2C241E] leading-relaxed font-semibold">
                                    "The air here smells of petrichor and fresh jasmine. We haven't slept this well in years."
                                  </p>
                                  <div className="flex items-center space-x-3 mt-6">
                                    <div className="w-8 h-8 rounded-full bg-[#6B705C]/20 border border-[#6B705C]/30 flex items-center justify-center font-bold text-xs text-[#6B705C]">
                                      A
                                    </div>
                                    <span className="text-xs font-mono font-bold text-[#6B705C]">
                                      — Aditi & Rohan, Bangalore
                                    </span>
                                  </div>
                                </div>
                              );
                            }
                          }

                          // Render memory card
                          const memory = item.memory;
                          const hasVideo = memory.mediaType === 'video';
                          
                          // Helper to safely parse media URLs list
                          const getMediaUrls = (url: string): string[] => {
                            try {
                              if (url && url.trim().startsWith('[')) {
                                const parsed = JSON.parse(url);
                                if (Array.isArray(parsed)) return parsed;
                              }
                            } catch (e) {}
                            return [url];
                          };
                          const mediaUrls = getMediaUrls(memory.mediaUrl);
                          const displayUrl = mediaUrls[0];
                          
                          return (
                            <div
                              key={`memory-card-${memory.id}-${index}`}
                              onClick={() => setSelectedMemory(memory)}
                              className="break-inside-avoid mb-8 bg-white border border-[#E5E2D9] rounded-3xl p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 flex flex-col cursor-pointer"
                            >
                              <div className="relative w-full overflow-hidden rounded-2xl bg-[#F4F1EB] mb-4 aspect-square">
                                {hasVideo ? (
                                  <video
                                    src={displayUrl}
                                    muted
                                    loop
                                    playsInline
                                    className="w-full h-full object-cover select-none"
                                  />
                                ) : (
                                  <img
                                    src={displayUrl}
                                    alt={memory.title}
                                    className="w-full h-full object-cover select-none"
                                    style={getImageStyleFromUrl(displayUrl)}
                                  />
                                )}

                                {/* Multiple photos indicator */}
                                {!hasVideo && mediaUrls.length > 1 && (
                                  <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-md text-white text-[9px] font-bold font-mono px-2 py-1 rounded-md border border-white/10 shadow-sm">
                                    +{mediaUrls.length - 1} photos
                                  </div>
                                )}

                                {/* Video indicator overlay */}
                                {hasVideo && (
                                  <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-md text-white rounded-full p-1.5 border border-white/10 shadow-sm">
                                    <Play size={10} fill="white" />
                                  </div>
                                )}
                              </div>
                              
                              <div className="px-1 py-2">
                                <span className="text-[10px] uppercase tracking-widest font-extrabold text-[#8E9288] mb-2 font-mono block">
                                  {memory.title}
                                </span>
                                {memory.description && (
                                  <p className="font-serif text-base italic text-[#2D2D2A] leading-relaxed line-clamp-3">
                                    "{memory.description}"
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {memoriesViewMode === 'timeline' && (() => {
                      // Sort memories descending by date
                      const sortedMemories = [...memories].sort((a, b) => b.date.localeCompare(a.date));

                      // Group by year
                      const groupedByYear: Record<string, Memory[]> = {};
                      sortedMemories.forEach((memory) => {
                        const year = memory.date ? memory.date.substring(0, 4) : 'Unknown';
                        if (!groupedByYear[year]) groupedByYear[year] = [];
                        groupedByYear[year].push(memory);
                      });
                      const sortedYears = Object.keys(groupedByYear).sort((a, b) => b.localeCompare(a));

                      return (
                        <div className="max-w-5xl mx-auto">
                          <div className="relative">
                            {/* Vertical spine line */}
                            <div className="absolute top-0 bottom-0 left-6 md:left-1/2 w-px bg-[#2D4A3E]/15 md:-translate-x-px" />

                            {sortedYears.map((year) => (
                              <div key={year} className="mb-12">
                                {/* Year marker pill */}
                                <div className="relative flex items-center mb-10">
                                  <div className="absolute left-6 md:left-1/2 -translate-x-1/2 z-10">
                                    <span className="inline-flex items-center gap-1.5 bg-[#2D2D2A] text-[#FAF7F2] text-xs font-bold px-4 py-1.5 rounded-full shadow-md font-mono tracking-wider">
                                      <Clock size={12} />
                                      {year}
                                    </span>
                                  </div>
                                </div>

                                {/* Memories for this year */}
                                {groupedByYear[year].map((memory, idx) => {
                                  const isLeft = idx % 2 === 0;
                                  const hasVideo = memory.mediaType === 'video';
                                  
                                  const getMediaUrls = (url: string): string[] => {
                                    try {
                                      if (url && url.trim().startsWith('[')) {
                                        const parsed = JSON.parse(url);
                                        if (Array.isArray(parsed)) return parsed;
                                      }
                                    } catch (e) {}
                                    return [url];
                                  };
                                  const mediaUrls = getMediaUrls(memory.mediaUrl);
                                  const displayUrl = mediaUrls[0];
                                  
                                  const formattedDate = () => {
                                    if (!memory.date) return '';
                                    try {
                                      const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
                                      return new Date(memory.date).toLocaleDateString('en-US', options);
                                    } catch {
                                      return memory.date;
                                    }
                                  };

                                  return (
                                    <div key={memory.id} className="relative mb-10 flex items-start">
                                      {/* Spine dot */}
                                      <div className="absolute left-6 md:left-1/2 -translate-x-1/2 z-10 mt-6">
                                        <div className="w-3.5 h-3.5 rounded-full border-2 border-white shadow-sm bg-[#2D4A3E]" />
                                      </div>

                                      {/* Card */}
                                      <div className={`
                                        ml-14 md:ml-0 w-full
                                        md:w-[calc(50%-2rem)]
                                        ${isLeft ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'}
                                      `}>
                                        <div
                                          onClick={() => setSelectedMemory(memory)}
                                          className="group bg-[#FAF7F2] border border-[#2D4A3E]/10 rounded-2xl overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                                        >
                                          {/* Thumbnail */}
                                          <div className="relative h-44 overflow-hidden bg-[#F4F1EB]">
                                            {hasVideo ? (
                                              <video
                                                src={displayUrl}
                                                muted
                                                loop
                                                playsInline
                                                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                              />
                                            ) : (
                                              <img
                                                src={displayUrl}
                                                alt={memory.title}
                                                referrerPolicy="no-referrer"
                                                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                                style={getImageStyleFromUrl(displayUrl)}
                                              />
                                            )}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />

                                            {/* Media Type / Photo count badge */}
                                            {!hasVideo && mediaUrls.length > 1 && (
                                              <span className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-[9px] font-bold font-mono px-2.5 py-1 rounded-md border border-white/10 shadow-sm">
                                                +{mediaUrls.length - 1} photos
                                              </span>
                                            )}
                                            {hasVideo && (
                                              <span className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm text-white rounded-full p-1.5 border border-white/10 shadow-sm">
                                                <Play size={10} fill="white" />
                                              </span>
                                            )}

                                            {/* Date badge */}
                                            {memory.date && (
                                              <span className="absolute bottom-3 left-3 bg-[#FAF7F2] text-[#2C241E] text-[10px] font-semibold px-2.5 py-1 rounded-md shadow-sm">
                                                {formattedDate()}
                                              </span>
                                            )}
                                          </div>

                                          {/* Card body */}
                                          <div className="p-5">
                                            <h3 className="font-serif text-lg font-bold text-[#2D2D2A] group-hover:text-[#2D4A3E] transition-colors leading-tight mb-2">
                                              {memory.title}
                                            </h3>
                                            {memory.description && (
                                              <p className="text-xs text-[#6B705C] leading-relaxed line-clamp-3">
                                                "{memory.description}"
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}
              </section>

              {/* Call to Action Banner */}
              <section className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                <div className="bg-[#2D1F10] rounded-3xl p-12 md:p-16 text-center shadow-lg relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-[#FAF7F2]/5 rounded-full blur-3xl pointer-events-none" />
                  <div className="absolute -bottom-10 -left-10 w-80 h-80 bg-[#FAF7F2]/3 rounded-full blur-3xl pointer-events-none" />
                  
                  <h2 className="font-serif text-3xl md:text-4xl text-[#EBE7DF] font-bold mb-8">
                    Ready to create your own memories?
                  </h2>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <button
                      onClick={() => {
                        const footer = document.getElementById('app-footer');
                        if (footer) footer.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="px-8 py-3.5 bg-[#D5E0C5] text-[#2D1F10] font-semibold text-sm rounded-full shadow-md hover:bg-[#c2ceb1] transition-all duration-300 font-mono w-full sm:w-auto"
                    >
                      Check Availability
                    </button>
                    <button
                      onClick={() => setCurrentPage('features')}
                      className="px-8 py-3.5 bg-transparent text-[#EBE7DF] font-semibold text-sm rounded-full border border-[#EBE7DF]/30 hover:bg-white/5 transition-all duration-300 font-mono w-full sm:w-auto"
                    >
                      View Experiences
                    </button>
                  </div>
                </div>
              </section>
            </div>
          );
        })()}

        {/* ========================================================= */}
        {/* 3. EVENTS VIEW (with Cards / Timeline toggle) */}
        {/* ========================================================= */}
        {currentPage === 'events' && (
          <div className="animate-fadeIn bg-[#FDFCF9] min-h-screen">
            {/* Page Header */}
            <div className="w-full py-16 sm:py-20 bg-[#FAF7F0] border-b border-[#E6E1D4]">
              <div className="max-w-7xl mx-auto px-6 md:px-12">
                <span className="estate-chip mb-3">
                  <Calendar size={12} className="text-amber-700" />
                  Traditions Diary
                </span>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                  <div>
                    <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-[#1A241F] tracking-tight mb-3">
                      Gatherings, Harvests &amp; Traditions
                    </h1>
                    <p className="font-sans text-sm sm:text-base text-[#65756C] max-w-xl">
                      A treasured log of family suppers beneath the ancient oak tree, solstice fires under star-filled skies, and upcoming seasonal retreats.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <section id="events-scheduler" className="max-w-7xl mx-auto px-6 md:px-12 py-12">
              {/* View Mode Toggle */}
              <div className="flex items-center justify-between flex-wrap gap-4 mb-12">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-[#65756C] uppercase tracking-wider">
                    Calendar View:
                  </span>
                  <span className="estate-chip text-[11px]">
                    {events.length} Gatherings Total
                  </span>
                </div>

                <div className="inline-flex items-center bg-[#FAF7F0] border border-[#E6E1D4] rounded-full p-1 gap-1">
                  <button
                    onClick={() => setEventsViewMode('cards')}
                    className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                      eventsViewMode === 'cards'
                        ? 'bg-[#14532D] text-white shadow-sm'
                        : 'text-[#65756C] hover:text-[#1A241F]'
                    }`}
                  >
                    <LayoutGrid size={13} />
                    Curated Cards
                  </button>
                  <button
                    onClick={() => setEventsViewMode('timeline')}
                    className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                      eventsViewMode === 'timeline'
                        ? 'bg-[#14532D] text-white shadow-sm'
                        : 'text-[#65756C] hover:text-[#1A241F]'
                    }`}
                  >
                    <Clock size={13} />
                    Timeline Journey
                  </button>
                </div>
              </div>

              {/* ---- CARDS VIEW ---- */}
              {eventsViewMode === 'cards' && (
                <div className="space-y-16">
                  
                  {/* 1. UPCOMING GATHERINGS */}
                  <div id="upcoming-meetings" className="space-y-6">
                    <div className="flex items-center justify-between border-b border-[#E6E1D4] pb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-800 flex items-center justify-center">
                          <Calendar size={16} />
                        </div>
                        <h2 className="font-serif text-2xl font-semibold text-[#1A241F]">
                          Upcoming Gatherings &amp; Seasonal Plans
                        </h2>
                      </div>
                      <span className="estate-chip text-[10px]">
                        {upcomingEvents.length} Planned
                      </span>
                    </div>

                    {upcomingEvents.length === 0 ? (
                      <div className="py-16 bg-white border border-[#E6E1D4] text-center rounded-3xl p-8">
                        <p className="font-serif italic text-base text-[#65756C]">
                          No upcoming gatherings scheduled right now. A restful season is ahead in Sirudavoor.
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {upcomingEvents.map((ev) => (
                          <div
                            key={ev.id}
                            onClick={() => setSelectedEvent(ev)}
                            className="card-estate bg-white border border-[#E6E1D4] flex flex-col sm:flex-row cursor-pointer group hover:border-emerald-700/30 transition-all duration-300"
                          >
                            <div className="sm:w-2/5 h-56 sm:h-auto relative overflow-hidden">
                              <img
                                src={ev.thumbnailUrl}
                                alt={ev.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                style={getImageStyleFromUrl(ev.thumbnailUrl)}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                              <span className="absolute bottom-3 left-3 stamp-badge">
                                <span className="text-[9px] font-bold uppercase text-amber-700">DATE</span>
                                <span className="font-serif text-xs font-bold text-stone-900">{ev.date}</span>
                              </span>
                            </div>
                            <div className="sm:w-3/5 p-6 flex flex-col justify-between">
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="estate-chip text-[10px]">
                                    Upcoming Gathering
                                  </span>
                                  {ev.type && (
                                    <span className="text-[11px] text-[#65756C] font-medium bg-[#FAF7F0] px-2.5 py-0.5 rounded-full border border-[#E6E1D4]">
                                      {ev.type}
                                    </span>
                                  )}
                                </div>
                                <h3 className="font-serif text-xl font-semibold text-[#1A241F] group-hover:text-[#14532D] transition-colors leading-snug mt-2">
                                  {ev.title}
                                </h3>
                                <p className="text-xs text-[#37463E] line-clamp-3 mt-3 leading-relaxed font-sans">
                                  {ev.description}
                                </p>
                              </div>
                              <div className="flex justify-between items-center border-t border-[#E6E1D4]/60 pt-4 mt-5">
                                <span className="text-xs font-semibold text-[#14532D] group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-1">
                                  <span>Read Gathering Story</span>
                                  <ArrowRight size={13} />
                                </span>
                                {ev.galleryUrls && ev.galleryUrls.length > 1 && (
                                  <span className="text-[10px] text-[#8E9E95] font-medium">
                                    +{ev.galleryUrls.length} photos
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 2. PAST TRADITIONS & MEMORIES */}
                  <div id="past-meetings" className="space-y-6">
                    <div className="flex items-center justify-between border-b border-[#E6E1D4] pb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-800 flex items-center justify-center">
                          <Eye size={16} />
                        </div>
                        <h2 className="font-serif text-2xl font-semibold text-[#1A241F]">
                          Past Traditions &amp; Celebrations
                        </h2>
                      </div>
                      <span className="estate-chip-amber text-[10px]">
                        {pastEvents.length} Historical Logs
                      </span>
                    </div>

                    {pastEvents.length === 0 ? (
                      <div className="py-16 bg-white border border-[#E6E1D4] text-center rounded-3xl p-8">
                        <p className="font-serif italic text-base text-[#65756C]">
                          No historical tradition entries recorded yet.
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {pastEvents.map((ev) => (
                          <div
                            key={ev.id}
                            onClick={() => setSelectedEvent(ev)}
                            className="card-estate bg-white border border-[#E6E1D4] flex flex-col cursor-pointer group hover:border-amber-700/30 transition-all duration-300"
                          >
                            <div className="h-56 relative overflow-hidden">
                              <img
                                src={ev.thumbnailUrl}
                                alt={ev.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                style={getImageStyleFromUrl(ev.thumbnailUrl)}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                              <span className="absolute bottom-4 left-4 stamp-badge shadow-md">
                                <span className="text-[9px] font-bold uppercase text-amber-700">SEASON</span>
                                <span className="font-serif text-xs font-bold text-stone-900">{ev.date}</span>
                              </span>
                              {ev.type && (
                                <span className="absolute top-4 right-4 bg-white/90 backdrop-blur-xs text-xs font-semibold px-3 py-1 rounded-full text-[#1A241F] shadow-xs">
                                  {ev.type}
                                </span>
                              )}
                            </div>
                            <div className="p-6 space-y-3 flex-grow flex flex-col justify-between">
                              <div>
                                <span className="text-[10px] text-amber-800 uppercase tracking-wider font-semibold block mb-1">
                                  Preserved Gathering
                                </span>
                                <h3 className="font-serif text-xl font-semibold text-[#1A241F] group-hover:text-[#14532D] transition-colors leading-snug">
                                  {ev.title}
                                </h3>
                                <p className="text-xs text-[#37463E] leading-relaxed font-sans line-clamp-3 mt-2">
                                  {ev.description}
                                </p>
                              </div>
                              <div className="border-t border-[#E6E1D4]/60 pt-4 flex justify-between items-center text-xs text-[#14532D] font-semibold">
                                <span className="group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-1">
                                  Explore Diary Entry <ArrowRight size={13} />
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                </div>
              )}

              {/* ---- TIMELINE VIEW (events only) ---- */}
              {eventsViewMode === 'timeline' && (() => {
                const sortedEvents = [...events].sort((a, b) => b.dateRaw.localeCompare(a.dateRaw));

                return (
                  <div className="max-w-3xl mx-auto relative py-8">
                    {/* Continuous vertical timeline stem */}
                    <div className="absolute left-6 md:left-1/2 -translate-x-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-emerald-600 via-emerald-800 to-amber-600/50" />

                    <div className="space-y-12">
                      {sortedEvents.map((ev, idx) => {
                        const isEven = idx % 2 === 0;
                        return (
                          <div
                            key={ev.id}
                            onClick={() => setSelectedEvent(ev)}
                            className="relative flex items-center md:justify-between group cursor-pointer"
                          >
                            {/* Marker dot */}
                            <div className="absolute left-6 md:left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-white border-4 border-[#14532D] z-10 shadow-md group-hover:scale-125 transition-transform" />

                            {/* Card positioning (alternating on desktop) */}
                            <div className={`ml-14 md:ml-0 md:w-[45%] ${isEven ? 'md:mr-auto md:text-right' : 'md:ml-auto md:text-left'}`}>
                              <div className="card-estate p-5 bg-white border border-[#E6E1D4] hover:border-emerald-700/40 transition-all duration-300">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  <span className={ev.category === 'upcoming' ? 'estate-chip text-[9px]' : 'estate-chip-amber text-[9px]'}>
                                    {ev.date}
                                  </span>
                                  {ev.type && (
                                    <span className="text-[10px] text-[#65756C] font-semibold">
                                      • {ev.type}
                                    </span>
                                  )}
                                </div>
                                <h3 className="font-serif text-lg font-semibold text-[#1A241F] group-hover:text-[#14532D] transition-colors mb-1">
                                  {ev.title}
                                </h3>
                                <p className="text-xs text-[#65756C] line-clamp-2 leading-relaxed">
                                  {ev.description}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </section>
          </div>
        )}

        {/* ========================================================= */}
        {/* 4. FEATURES VIEW */}
        {/* ========================================================= */}
        {currentPage === 'features' && (
          <div className="animate-fadeIn bg-[#FDFCF9] min-h-screen">
            {/* Page Header */}
            <div className="w-full py-16 sm:py-20 bg-[#FAF7F0] border-b border-[#E6E1D4]">
              <div className="max-w-7xl mx-auto px-6 md:px-12">
                <span className="estate-chip mb-3">
                  <Sprout size={12} className="text-emerald-700" />
                  Sanctuary Grounds
                </span>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                  <div>
                    <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-[#1A241F] tracking-tight mb-3">
                      The Estate, Groves &amp; Quiet Nooks
                    </h1>
                    <p className="font-sans text-sm sm:text-base text-[#65756C] max-w-xl">
                      A collection of thoughtfully preserved spaces and country amenities designed to nurture connections, rest, and a slow, intentional pace of life.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <section id="house-features" className="max-w-7xl mx-auto px-6 md:px-12 py-16">
              {/* Features Responsive Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {features.map((feat) => (
                  <div
                    key={feat.id}
                    onClick={() => setSelectedFeature(feat)}
                    className="card-estate bg-white border border-[#E6E1D4] overflow-hidden flex flex-col md:flex-row h-auto md:h-72 cursor-pointer group hover:border-emerald-700/30 transition-all duration-300"
                  >
                    <div className="md:w-1/2 h-56 md:h-auto overflow-hidden relative">
                      <img 
                        src={feat.imageUrl} 
                        alt={feat.name} 
                        referrerPolicy="no-referrer"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                        style={getImageStyleFromUrl(feat.imageUrl)}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                      {feat.tag && (
                        <span className="absolute bottom-3 left-3 estate-chip-amber text-[10px] shadow-sm">
                          {feat.tag}
                        </span>
                      )}
                    </div>
                    
                    <div className="md:w-1/2 p-6 sm:p-7 flex flex-col justify-between">
                      <div>
                        <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-800 flex items-center justify-center mb-3">
                          <Sprout size={16} />
                        </div>
                        <h3 className="font-serif text-xl font-semibold text-[#1A241F] group-hover:text-[#14532D] transition-colors leading-snug mb-2">
                          {feat.name}
                        </h3>
                        <p className="text-xs text-[#37463E] leading-relaxed font-sans line-clamp-4">
                          {feat.description}
                        </p>
                      </div>

                      <div className="pt-4 border-t border-[#E6E1D4]/60 mt-4 flex items-center justify-between text-xs text-[#14532D] font-semibold">
                        <span className="group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-1">
                          Explore Grounds Detail <ArrowRight size={13} />
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {/* ========================================================= */}
        {/* 5. ADMIN / LOGIN VIEW */}
        {/* ========================================================= */}
        {currentPage === 'admin' && (
          <div className="animate-fadeIn">
            {!isAdmin ? (
              // Login Screen — Deep Botanical Card
              <div className="min-h-[85vh] flex items-center justify-center px-4 py-16 bg-[#FAF7F0]">
                <div className="w-full max-w-md bg-gradient-to-br from-[#0F281C] via-[#143B2A] to-[#0A1E15] border border-emerald-500/20 rounded-3xl p-8 sm:p-10 text-white shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
                  
                  <div className="text-center mb-8 relative z-10">
                    <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center mx-auto mb-4 text-emerald-300 shadow-md">
                      <Lock size={22} />
                    </div>
                    <span className="estate-chip text-[10px] mb-2 shadow-xs">
                      Family Verification
                    </span>
                    <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-white tracking-tight mb-2">
                      Estate Passcode
                    </h1>
                    <p className="text-xs text-stone-300 leading-relaxed max-w-xs mx-auto">
                      Enter the private credentials to manage memories, traditions, and sanctuary settings.
                    </p>
                  </div>

                  <form onSubmit={handleAdminLoginSubmit} className="relative z-10 space-y-4">
                    {passwordError && (
                      <div className="p-3 bg-red-900/40 border border-red-500/40 rounded-xl text-red-200 text-xs text-center">
                        {passwordError}
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-emerald-200/80 mb-2">
                        Private Passcode
                      </label>
                      <input
                        type="password"
                        placeholder="Enter passcode..."
                        value={adminPasswordInput}
                        onChange={(e) => setAdminPasswordInput(e.target.value)}
                        required
                        className="w-full bg-white/10 border border-white/20 focus:border-amber-400 focus:ring-1 focus:ring-amber-400/50 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-all"
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn-estate-gold w-full justify-center text-xs py-3 font-semibold shadow-lg mt-2"
                    >
                      Authenticate &amp; Open Workspace
                    </button>
                  </form>
                </div>
              </div>
            ) : (
              // Full Workspace Panel
              <AdminPanel
                memories={memories}
                events={events}
                features={features}
                homeSettings={homeSettings}
                onUpdateMemories={handleUpdateMemories}
                onUpdateEvents={handleUpdateEvents}
                onUpdateFeatures={handleUpdateFeatures}
                onUpdateHomeSettings={handleUpdateHomeSettings}
                onResetData={handleResetData}
                onLogout={handleLogout}
                onRefreshAllData={loadAllDataFromMySQL}
              />
            )}
          </div>
        )}

      </main>

      {/* Footer component */}
      <Footer onShowModal={(type) => setFooterModalType(type)} />

      {/* Event details detail dialog */}
      <EventDetailModal
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />

      {/* Memory details detail dialog */}
      <MemoryDetailModal
        memory={selectedMemory}
        onClose={() => setSelectedMemory(null)}
      />

      {/* Footer Detail Information Overlay Modals */}
      {footerModalType && (
        <div
          onClick={() => setFooterModalType(null)}
          className="fixed inset-0 z-50 bg-stone-950/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 animate-fadeIn"
        >
          <div className="bg-[#FDFCF9] text-[#1A241F] w-full max-w-lg rounded-3xl p-6 sm:p-8 animate-scaleUp border border-[#E6E1D4] shadow-2xl relative">
            <button
              onClick={() => setFooterModalType(null)}
              className="absolute top-5 right-5 p-1.5 hover:bg-stone-100 rounded-full cursor-pointer text-[#65756C] transition-colors"
              title="Close"
            >
              <X size={18} />
            </button>

            {/* CONTACT MODAL */}
            {footerModalType === 'contact' && (
              <div className="space-y-4">
                <div className="w-12 h-12 bg-emerald-50 text-emerald-800 rounded-2xl flex items-center justify-center border border-emerald-200/60">
                  <Phone size={20} />
                </div>
                <div>
                  <span className="estate-chip text-[10px] mb-2">Private Family Sanctuary</span>
                  <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-[#1A241F]">Contact Concierge</h3>
                </div>
                <p className="text-sm text-[#37463E] leading-relaxed">
                  Mithran Farm House is a private estate sanctuary for our extended family and close circle. It is not open for public commercial bookings or outside tourism.
                </p>
                <div className="p-4 bg-[#FAF7F0] text-[#14532D] rounded-2xl border border-[#E6E1D4] space-y-1.5 text-xs">
                  <span className="font-semibold block text-stone-900">Family &amp; Guest Coordination:</span>
                  <span className="block text-[#37463E]">Reach out directly in the family coordination group or contact the caretaker manager on property.</span>
                </div>
              </div>
            )}

            {/* DIRECTIONS MODAL */}
            {footerModalType === 'directions' && (
              <div className="space-y-4">
                <div className="w-12 h-12 bg-amber-50 text-amber-800 rounded-2xl flex items-center justify-center border border-amber-200/60">
                  <MapPin size={20} />
                </div>
                <div>
                  <span className="estate-chip-amber text-[10px] mb-2">Location &amp; Access</span>
                  <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-[#1A241F]">Directions to Sirudavoor</h3>
                </div>
                <p className="text-sm text-[#37463E] leading-relaxed">
                  The farm house is nestled amidst lush private acreage in Sirudavoor, Chengalpattu district.
                </p>
                <div className="p-4 bg-[#FAF7F0] border border-[#E6E1D4] rounded-2xl text-xs space-y-2">
                  <span className="font-semibold block text-amber-800">📍 GPS Coordinates:</span>
                  <code className="block bg-white p-2 rounded-lg text-xs font-mono select-all border border-[#E6E1D4]">
                    12.6819° N, 79.9762° E · Sirudavoor, Chengalpattu
                  </code>
                  <span className="block text-[#65756C] leading-relaxed mt-2">
                    Follow the Chengalpattu-Sirudavoor country road, pass the village grove, turn at the marked avenue of palm trees, and enter through the iron estate gates.
                  </span>
                </div>
                
                <a 
                  href="https://maps.app.goo.gl/VCtYoxuA4cLNtSZHA" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-estate-primary w-full justify-center py-3 text-xs gap-2"
                >
                  <MapPin size={15} />
                  <span>Open in Google Maps Directions</span>
                </a>
              </div>
            )}

            {/* PRIVACY MODAL */}
            {footerModalType === 'privacy' && (
              <div className="space-y-4">
                <div className="w-12 h-12 bg-emerald-50 text-emerald-800 rounded-2xl flex items-center justify-center border border-emerald-200/60">
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <span className="estate-chip text-[10px] mb-2">Family Archive Code</span>
                  <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-[#1A241F]">Privacy &amp; Archives Policy</h3>
                </div>
                <p className="text-sm text-[#37463E] leading-relaxed">
                  We deeply cherish the sanctity and privacy of our family. This website is a dedicated memory space: we do not serve commercial advertising, sell user data, or share family photographs with third parties.
                </p>
                <div className="p-4 bg-[#FAF7F0] rounded-2xl border border-[#E6E1D4] text-xs text-[#65756C]">
                  All media and records are stored securely in our private MySQL archive for generations to come.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Feature Details Modal */}
      <FeatureDetailModal 
        feature={selectedFeature} 
        onClose={() => setSelectedFeature(null)} 
      />

    </div>
  );
}
